import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import { useAnchoredDropdown } from '../common/useAnchoredDropdown';

import type { SortMode } from './useClipSort';

type Props = {
  sortMode: SortMode;
  onSortChange: (mode: SortMode) => void;
};

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
  { value: 'impact_score', label: 'Impact Score' },
  { value: 'start_time', label: 'Start Time' },
];

/** Wide enough for "Impact Score" — the hook otherwise inherits the trigger's width. */
const MENU_WIDTH = 160;

export default function ClipSortDropdown({ sortMode, onSortChange }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { menuRef, style } = useAnchoredDropdown(
    isOpen,
    triggerRef,
    () => setIsOpen(false),
    undefined,
    { width: MENU_WIDTH },
  );

  const selectedLabel =
    SORT_OPTIONS.find(opt => opt.value === sortMode)?.label || '';

  const handleSelect = (value: SortMode) => {
    onSortChange(value);
    setIsOpen(false);
  };

  return (
    <Wrapper data-testid="clip-results-sort">
      <TriggerButton
        ref={triggerRef}
        onClick={() => setIsOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Sort clips"
      >
        <TriggerLabel>{selectedLabel}</TriggerLabel>
        <Caret $isOpen={isOpen}>
          <ChevronDownIcon
            width="10"
            height="10"
            strokeColor="currentColor"
            strokeWidth="2"
          />
        </Caret>
      </TriggerButton>
      {isOpen &&
        style &&
        createPortal(
          <Menu ref={menuRef} style={style} role="menu">
            {SORT_OPTIONS.map(option => (
              <MenuItem
                key={option.value}
                role="menuitem"
                $isSelected={option.value === sortMode}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>,
          document.body,
        )}
    </Wrapper>
  );
}

// No `position: relative` — the menu is portaled to <body> and positioned by the
// hook, so anchoring it here would do nothing.
const Wrapper = styled.div``;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 4px 8px;
  border-radius: var(--radius);
  cursor: pointer;
  transition: background-color var(--transition-fast) ease-out;

  &:hover {
    background-color: var(--surface-elevated);
  }

  &:active {
    scale: 0.98;
  }
`;

const TriggerLabel = styled.span`
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: 100%;
`;

/** Points down when closed, flips up when open. */
const Caret = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  color: var(--accent);
  transition: transform var(--transition-fast) ease;
  ${props =>
    props.$isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;

// Rounded-row menu: padding on the container, a gap between rows, and no
// full-width dividers.
const Menu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--spacing-xs);
  background-color: var(--popover);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius);
  box-shadow: var(--elevation-toast);
  font-family: var(--font-primary);
`;

const MenuItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  min-height: 32px;
  padding: 0 10px;
  border-radius: 6px;
  white-space: nowrap;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  /* Gold for the selected value — never green, which is for actions. */
  color: ${props =>
    props.$isSelected ? 'var(--accent)' : 'var(--text-secondary)'};

  &:hover {
    background-color: var(--surface-elevated);
    color: ${props =>
      props.$isSelected ? 'var(--accent)' : 'var(--foreground)'};
  }
`;
